import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Landmark, FileText, RefreshCw, Download, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PaymentStateBadge, ReasonDialog, SeatRequired, Tabs, XeroSyncBadge } from '../../components/nursery/accounts/AccountBits';
import { AcceptShortPaymentDialog } from '../../components/nursery/accounts/AcceptShortPaymentDialog';
import { RecordPaymentDialog } from '../../components/nursery/accounts/RecordPaymentDialog';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { money, num, pct } from '../../utils/nurseryMoney';
import { cn } from '../../utils/cn';
import type { ApiResponse } from '../../types';
import {
  CREDIT_REASON_LABELS, PAYMENT_METHOD_LABELS,
  type AgedDebtors, type Ageing, type CreditNote, type ExpectedPayments,
  type Payment, type PaymentSource, type ShortPay,
} from '../../types/nurseryAccounts';

/**
 * /accounts/receivables: who owes what, what should arrive next, what was paid
 * short, and the payments and credit notes behind those figures. The tab is in
 * `?tab=` so a refresh or a shared link lands on the same list.
 */

type TabKey = 'aged' | 'expected' | 'short' | 'payments' | 'credits';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'aged', label: 'Aged debtors' },
  { key: 'expected', label: 'Expected' },
  { key: 'short', label: 'Short payments' },
  { key: 'payments', label: 'Payments' },
  { key: 'credits', label: 'Credit notes' },
];

const PAYMENT_SOURCE_LABELS: Record<PaymentSource, string> = {
  xero: 'From Xero',
  stripe: 'Paid online',
  manual: 'Entered here',
  legacy: 'Carried over from the old record',
  counter: 'Paid at the counter',
  remittance: 'Recorded remittance',
};

interface TabProps {
  tz: string;
  isAdmin: boolean;
  xeroConnected: boolean;
  /** Bumped to make the tab load again (after Check Xero). */
  reloadKey: number;
}

/** Run a reason-dialog action. Throwing keeps the dialog open with the message. */
async function confirmed(fallback: string, fn: () => Promise<ApiResponse<unknown>>): Promise<void> {
  let res: ApiResponse<unknown>;
  try {
    res = await fn();
  } catch (e) {
    throw new Error(errorMessage(e, fallback));
  }
  if (!res.success) throw new Error(errorMessage(res, fallback));
}

const shortDate = (d: string | null | undefined, tz: string) => (d ? formatLocalDateInTz(d.slice(0, 10), tz) : null);

function ErrorBox({ children }: { children: ReactNode }) {
  return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{children}</div>;
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500 break-words">{children}</div>;
}

function Skeleton() {
  return <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />;
}

function Marker({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'amber' }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
      tone === 'amber' ? 'bg-amber-100 text-amber-800' : 'bg-neutral-100 text-neutral-600')}>
      {children}
    </span>
  );
}

// ── Aged debtors ─────────────────────────────────────────────────────────────

const AGE_COLUMNS: { key: keyof Ageing; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30', label: '1–30 days' },
  { key: 'd31_60', label: '31–60 days' },
  { key: 'd61_90', label: '61–90 days' },
  { key: 'd90_plus', label: '90+ days' },
  { key: 'total', label: 'Total' },
];

/** The ageing figures as a small labelled grid: two across on a phone, six on a wide screen. */
function AgeingGrid({ ageing, small }: { ageing: Ageing; small?: boolean }) {
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-4 gap-y-2">
      {AGE_COLUMNS.map((c) => {
        const late = c.key !== 'current' && c.key !== 'total' && num(ageing[c.key]) > 0;
        return (
          <div key={c.key} className="min-w-0">
            <dt className="text-xs text-neutral-500">{c.label}</dt>
            <dd className={cn(small ? 'text-sm' : 'text-base', c.key === 'total' ? 'font-bold text-neutral-900' : 'font-medium',
              late ? 'text-red-700' : 'text-neutral-900')}>
              {money(ageing[c.key])}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function AgedTab({ tz, reloadKey }: TabProps) {
  const [data, setData] = useState<AgedDebtors | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    nurseryAccountsService.getAgedDebtors()
      .then((res) => {
        if (cancelled) return;
        if (res.success) setData(res.data);
        else setError(errorMessage(res, 'Could not load the aged debtors'));
      })
      .catch((e) => { if (!cancelled) setError(errorMessage(e, 'Could not load the aged debtors')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (loading) return <Skeleton />;
  if (error) return <ErrorBox>{error}</ErrorBox>;
  if (!data || data.payers.length === 0) return <Empty>Nobody owes anything. Every authorised invoice has been paid or credited.</Empty>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 break-words">
        What each account owes{data.as_at ? ` as at ${shortDate(data.as_at, tz)}` : ''}, by how long past its due date. A site's invoices
        are counted under the head office that pays them.
      </p>
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-2">Everyone</h2>
        <AgeingGrid ageing={data.totals} />
      </div>

      <ul className="space-y-3">
        {data.payers.map((p) => {
          const expanded = !!open[p.key];
          return (
            <li key={p.key} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="font-semibold text-neutral-900 break-words min-w-0">
                  {p.payer_id != null
                    ? <Link to={`/accounts/customers/${p.payer_id}?tab=account`} className="text-primary-700 hover:underline">{p.payer_name}</Link>
                    : p.payer_name}
                </h3>
                <span className="text-xs text-neutral-500">{p.invoices} invoice{p.invoices === 1 ? '' : 's'}</span>
              </div>
              <AgeingGrid ageing={p} />
              {num(p.on_account) > 0 && (
                <p className="text-sm text-neutral-700 break-words">
                  {money(p.on_account)} of credit is on account and not yet set against an invoice, so the net owing
                  is <span className="font-semibold">{money(p.net)}</span>.
                </p>
              )}
              {p.over_limit && (
                <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
                  Over its credit limit of {money(p.credit_limit)}.
                </p>
              )}
              {num(p.disputed) > 0 && (
                <p className="text-sm text-amber-800 break-words">{money(p.disputed)} in dispute.</p>
              )}
              {p.sites.length > 0 && (
                <div>
                  <button type="button" aria-expanded={expanded}
                    onClick={() => setOpen((o) => ({ ...o, [p.key]: !expanded }))}
                    className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {expanded ? 'Hide' : 'Show'} its {p.sites.length} site{p.sites.length === 1 ? '' : 's'}
                  </button>
                  {expanded && (
                    <ul className="mt-2 space-y-2">
                      {p.sites.map((s, i) => (
                        <li key={s.customer_id ?? `none-${i}`} className="bg-neutral-50 rounded-lg p-3 space-y-2">
                          <p className="text-sm font-medium text-neutral-800 break-words">
                            {s.customer_id != null
                              ? <Link to={`/accounts/customers/${s.customer_id}`} className="hover:underline">{s.customer_name}</Link>
                              : s.customer_name}
                            {s.customer_code ? <span className="text-neutral-500"> #{s.customer_code}</span> : null}
                          </p>
                          <AgeingGrid ageing={s} small />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Expected ─────────────────────────────────────────────────────────────────

function ExpectedTab({ tz, reloadKey }: TabProps) {
  const [by, setBy] = useState('');
  const [data, setData] = useState<ExpectedPayments | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    nurseryAccountsService.getExpectedPayments(by ? { by } : {})
      .then((res) => {
        if (cancelled) return;
        if (res.success) setData(res.data);
        else setError(errorMessage(res, 'Could not load the expected payments'));
      })
      .catch((e) => { if (!cancelled) setError(errorMessage(e, 'Could not load the expected payments')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [by, reloadKey]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 break-words">
        What each account should pay next. The total is the figure the next deposit should be, and the list is what to
        match it against in Xero.
      </p>
      <div className="sm:max-w-xs">
        <label htmlFor="expected-by" className="block text-sm font-medium text-neutral-700 mb-1">Invoices due by</label>
        <input id="expected-by" type="date" value={by} onChange={(e) => setBy(e.target.value)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        {!by && data?.by && (
          <p className="text-xs text-neutral-500 mt-1">Showing what falls due by {shortDate(data.by, tz)}.</p>
        )}
      </div>

      {loading ? <Skeleton /> : error ? <ErrorBox>{error}</ErrorBox> : !data || data.payers.length === 0 ? (
        <Empty>Nothing falls due by that date.</Empty>
      ) : (
        <ul className="space-y-3">
          {data.payers.map((p, i) => (
            <li key={p.payer_id ?? `none-${i}`} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="font-semibold text-neutral-900 break-words min-w-0">
                  {p.payer_id != null
                    ? <Link to={`/accounts/customers/${p.payer_id}?tab=account`} className="text-primary-700 hover:underline">{p.payer_name}</Link>
                    : p.payer_name}
                </h3>
                <span className="ml-auto text-lg font-bold text-neutral-900">{money(p.total)}</span>
              </div>
              {num(p.overdue) > 0 && (
                <p className="text-sm text-red-700 break-words">{money(p.overdue)} of that is already overdue.</p>
              )}
              {p.settlement_discount && (
                <p className="text-sm text-neutral-700 break-words">
                  {pct(p.settlement_discount.rate_pct)} off if paid within {p.settlement_discount.days} days, so the deposit
                  may be smaller than this total.
                </p>
              )}
              <ul className="divide-y divide-neutral-100 border-t border-neutral-100">
                {p.invoices.map((inv) => (
                  <li key={inv.id}>
                    <Link to={`/accounts/invoices/${inv.id}`} className="block py-2 hover:bg-neutral-50 rounded">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-primary-700 break-words min-w-0">{inv.invoice_number}</span>
                        {inv.payment_state !== 'open' && <PaymentStateBadge state={inv.payment_state} />}
                        {inv.dispute_status === 'open' && <Marker tone="amber">Disputed</Marker>}
                        <span className="ml-auto font-semibold text-neutral-900">{money(inv.balance)}</span>
                      </div>
                      <div className="text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {(inv.customer_name || inv.customer_code) && (
                          <span className="break-words min-w-0">
                            {inv.customer_name}{inv.customer_code ? ` #${inv.customer_code}` : ''}
                          </span>
                        )}
                        {inv.customer_po && <span className="break-words min-w-0">PO {inv.customer_po}</span>}
                        <span>{inv.due_date ? `Due ${shortDate(inv.due_date, tz)}` : 'No due date'}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Short payments ───────────────────────────────────────────────────────────

function ShortPaysTab({ tz, isAdmin, reloadKey }: TabProps) {
  const [rows, setRows] = useState<ShortPay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<ShortPay | null>(null);
  const [disputing, setDisputing] = useState<ShortPay | null>(null);
  const [resolving, setResolving] = useState<ShortPay | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryAccountsService.getShortPays();
      if (res.success) setRows(res.data || []);
      else setError(errorMessage(res, 'Could not load the short payments'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the short payments'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 break-words">
        Invoices past their due date that still owe something. Each one is a short payment, a claim the customer has
        made, or an invoice the customer never received. Accept the shortfall, dispute it, or chase it.
      </p>
      {error && <ErrorBox>{error}</ErrorBox>}
      {loading ? <Skeleton /> : rows.length === 0 ? (
        !error && <Empty>No invoice is past its due date with money still owing.</Empty>
      ) : (
        <ul className="space-y-3">
          {rows.map((inv) => {
            const isSite = inv.payer_id != null && inv.customer_id != null && inv.payer_id !== inv.customer_id;
            const disputeOpen = inv.dispute_status === 'open';
            return (
              <li key={inv.id} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Link to={`/accounts/invoices/${inv.id}`} className="font-semibold text-primary-700 hover:underline break-words min-w-0">
                    {inv.invoice_number}
                  </Link>
                  {num(inv.amount_paid) > 0 && <Marker tone="amber">Part paid</Marker>}
                  {disputeOpen && <Marker tone="amber">Disputed</Marker>}
                  <span className="ml-auto text-sm font-medium text-red-700">
                    {inv.days_overdue} day{inv.days_overdue === 1 ? '' : 's'} overdue
                  </span>
                </div>
                <p className="text-sm text-neutral-800 break-words">
                  {inv.payer_name || inv.customer_name}
                  {isSite && <span className="text-neutral-600"> · {inv.customer_name}{inv.customer_code ? ` #${inv.customer_code}` : ''}</span>}
                </p>
                <p className="text-xs text-neutral-500 break-words">
                  {inv.customer_po ? `PO ${inv.customer_po} · ` : ''}Was due {shortDate(inv.due_date, tz)}
                </p>
                <dl className="grid grid-cols-3 gap-x-3 text-sm">
                  <div className="min-w-0"><dt className="text-xs text-neutral-500">Total</dt><dd className="font-medium">{money(inv.total_amount)}</dd></div>
                  <div className="min-w-0"><dt className="text-xs text-neutral-500">Paid</dt><dd className="font-medium">{money(inv.amount_paid)}</dd></div>
                  <div className="min-w-0"><dt className="text-xs text-neutral-500">Balance</dt><dd className="font-bold">{money(inv.balance)}</dd></div>
                </dl>
                {disputeOpen && inv.dispute_note && (
                  <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
                    In dispute: {inv.dispute_note}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {isAdmin && <Button size="sm" variant="outline" onClick={() => setAccepting(inv)}>Accept</Button>}
                  {disputeOpen
                    ? <Button size="sm" variant="outline" onClick={() => setResolving(inv)}>Resolve</Button>
                    : <Button size="sm" variant="outline" onClick={() => setDisputing(inv)}>Dispute</Button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {accepting && (
        <AcceptShortPaymentDialog invoice={accepting} onClose={() => setAccepting(null)}
          onDone={() => { setAccepting(null); load(); }} />
      )}
      {disputing && (
        <ReasonDialog
          title={`Dispute ${disputing.invoice_number}`}
          body="This marks the invoice as disputed, so it stands apart in these lists while it is sorted out. What is owed does not change."
          label="What the customer disputes"
          confirmLabel="Mark as disputed"
          onClose={() => setDisputing(null)}
          onConfirm={async (note) => {
            await confirmed('Could not mark the invoice as disputed', () => nurseryAccountsService.openDispute(disputing.id, note));
            setDisputing(null);
            load();
          }}
        />
      )}
      {resolving && (
        <ReasonDialog
          title={`Resolve the dispute on ${resolving.invoice_number}`}
          body="This closes the dispute. If something was agreed to come off the invoice, accept the short payment or raise a credit note for it as well."
          label="How it was resolved"
          confirmLabel="Resolve"
          onClose={() => setResolving(null)}
          onConfirm={async (note) => {
            await confirmed('Could not resolve the dispute', () => nurseryAccountsService.resolveDispute(resolving.id, note));
            setResolving(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ── Payments ─────────────────────────────────────────────────────────────────

function PaymentsTab({ tz, isAdmin, xeroConnected, reloadKey }: TabProps) {
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [voiding, setVoiding] = useState<Payment | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryAccountsService.getPayments();
      if (res.success) setRows(res.data || []);
      else setError(errorMessage(res, 'Could not load the payments'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the payments'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <div className="space-y-4">
      {xeroConnected ? (
        <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 text-sm text-sky-900 break-words">
          Xero is connected, so banked payments are matched to the deposit there and come back here. Cash taken against an invoice is recorded on that invoice and sent to Xero from its cash-on-hand account.
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-neutral-600 break-words min-w-0">
            Each payment received, and the invoices it was set against.
          </p>
          {isAdmin && (
            <Button size="sm" onClick={() => setRecording(true)}>
              <Plus className="h-4 w-4 mr-1" /> Record a payment
            </Button>
          )}
        </div>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {loading ? <Skeleton /> : rows.length === 0 ? (
        !error && <Empty>No payments have been recorded yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {rows.map((p) => {
            const isVoid = p.status === 'void';
            return (
              <li key={p.id} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold text-neutral-900">{shortDate(p.payment_date, tz)}</span>
                  {isVoid && <Marker>Void</Marker>}
                  <span className={cn('ml-auto text-lg font-bold', isVoid ? 'line-through text-neutral-500' : 'text-neutral-900')}>
                    {money(p.amount)}
                  </span>
                </div>
                <p className="text-sm text-neutral-800 break-words">{p.payer_name || 'No account'}</p>
                <p className="text-xs text-neutral-500 break-words">
                  {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                  {p.reference ? ` · ${p.reference}` : ''}
                  {' · '}{PAYMENT_SOURCE_LABELS[p.source] ?? p.source}
                </p>
                {p.allocations.length > 0 && (
                  <ul className="text-sm border-t border-neutral-100 pt-2 space-y-1">
                    {p.allocations.map((a) => (
                      <li key={a.invoice_id} className="flex justify-between gap-3">
                        <Link to={`/accounts/invoices/${a.invoice_id}`} className="text-primary-700 hover:underline break-words min-w-0">
                          {a.invoice_number}
                        </Link>
                        <span className="whitespace-nowrap">{money(a.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {!isVoid && num(p.unallocated) > 0 && (
                  <p className="text-sm text-amber-800 break-words">{money(p.unallocated)} left on account.</p>
                )}
                {p.notes && <p className="text-xs text-neutral-500 break-words">{p.notes}</p>}
                {isVoid && p.void_reason && <p className="text-xs text-neutral-500 break-words">Voided: {p.void_reason}</p>}
                {isAdmin && !isVoid && p.source === 'manual' && (
                  <div className="pt-1">
                    <Button size="sm" variant="outline" onClick={() => setVoiding(p)}>Void</Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {recording && (
        <RecordPaymentDialog today={getTodayInTz(tz)} timezone={tz} onClose={() => setRecording(false)}
          onDone={() => { setRecording(false); load(); }} />
      )}
      {voiding && (
        <ReasonDialog
          danger
          title="Void this payment"
          body={`The payment of ${money(voiding.amount)} from ${voiding.payer_name || 'this account'} will be removed, and the invoices it was set against will owe that money again.`}
          label="Why it is being voided"
          confirmLabel="Void payment"
          onClose={() => setVoiding(null)}
          onConfirm={async (reason) => {
            await confirmed('Could not void the payment', () => nurseryAccountsService.voidPayment(voiding.id, reason));
            setVoiding(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ── Credit notes ─────────────────────────────────────────────────────────────

function CreditNotesTab({ tz, isAdmin, reloadKey }: TabProps) {
  const [rows, setRows] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [voiding, setVoiding] = useState<CreditNote | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryAccountsService.getCreditNotes();
      if (res.success) setRows(res.data || []);
      else setError(errorMessage(res, 'Could not load the credit notes'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the credit notes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const pdf = async (note: CreditNote) => {
    setBusy(note.id);
    setError(null);
    try {
      downloadBlob(await nurseryAccountsService.getCreditNotePdf(note.id), `${note.credit_note_number}.pdf`);
    } catch (e) {
      setError(errorMessage(e, 'Could not download the PDF'));
    }
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 break-words">
        Every credit note. A rebate is credited against each invoice when the invoice is authorised; the others are
        raised from an invoice's page.
      </p>
      {error && <ErrorBox>{error}</ErrorBox>}
      {loading ? <Skeleton /> : rows.length === 0 ? (
        !error && <Empty>No credit notes have been raised yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {rows.map((note) => {
            const isVoid = note.status === 'void';
            // Raised by a person and not yet in Xero: the only kind that can be withdrawn here.
            const canVoid = isAdmin && !isVoid && note.reason !== 'rebate' && !note.rebate_agreement_id
              && note.xero_sync_status !== 'synced' && !note.xero_credit_note_id;
            return (
              <li key={note.id} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className={cn('font-semibold break-words min-w-0', isVoid ? 'line-through text-neutral-500' : 'text-neutral-900')}>
                    {note.credit_note_number}
                  </span>
                  {isVoid && <Marker>Void</Marker>}
                  <XeroSyncBadge status={note.xero_sync_status} />
                  <span className={cn('ml-auto font-bold', isVoid ? 'line-through text-neutral-500' : 'text-neutral-900')}>
                    {money(note.total_amount)}
                  </span>
                </div>
                <p className="text-sm text-neutral-800 break-words">
                  {note.customer_name || 'No customer'}{note.customer_code ? ` #${note.customer_code}` : ''}
                </p>
                <p className="text-xs text-neutral-500 break-words">
                  {shortDate(note.credit_date, tz)} · {CREDIT_REASON_LABELS[note.reason] ?? note.reason}
                  {note.rebate_agreement_name ? `: ${note.rebate_agreement_name}` : ''}
                </p>
                <p className="text-sm text-neutral-700 break-words">
                  {note.invoice_id != null
                    ? <>Against <Link to={`/accounts/invoices/${note.invoice_id}`} className="text-primary-700 hover:underline">{note.invoice_number || 'its invoice'}</Link></>
                    : 'On account'}
                </p>
                {isVoid && note.void_reason && <p className="text-xs text-neutral-500 break-words">Voided: {note.void_reason}</p>}
                {note.xero_sync_status === 'failed' && note.xero_sync_error && (
                  <p className="text-xs text-red-700 break-words">{note.xero_sync_error}</p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" disabled={busy === note.id} aria-label={`PDF of ${note.credit_note_number}`}
                    onClick={() => pdf(note)}>
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  {canVoid && <Button size="sm" variant="outline" onClick={() => setVoiding(note)}>Void</Button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {voiding && (
        <ReasonDialog
          danger
          title={`Void ${voiding.credit_note_number}`}
          body={`The credit of ${money(voiding.total_amount)} will be withdrawn, and the invoice it was against will owe that money again.`}
          label="Why it is being voided"
          confirmLabel="Void credit note"
          onClose={() => setVoiding(null)}
          onConfirm={async (reason) => {
            await confirmed('Could not void the credit note', () => nurseryAccountsService.voidCreditNote(voiding.id, reason));
            setVoiding(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function Receivables() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');

  const [xeroConnected, setXeroConnected] = useState(false);
  const [checking, setChecking] = useState(false);
  const [xeroError, setXeroError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const requested = params.get('tab');
  const tab: TabKey = TABS.some((t) => t.key === requested) ? (requested as TabKey) : 'aged';

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    // Without an answer the page behaves as though Xero is not connected.
    nurseryAccountsService.getXeroStatus()
      .then((res) => {
        if (!cancelled && res.success && res.data) setXeroConnected(!!res.data.connected && !!res.data.tenant_selected);
      })
      .catch(() => { /* not connected, as far as this page can tell */ });
    return () => { cancelled = true; };
  }, [canManage]);

  const checkXero = async () => {
    setChecking(true);
    setXeroError(null);
    try {
      const res = await nurseryAccountsService.syncXero();
      if (res.success) {
        const lastError = res.data?.status?.last_sync_error;
        if (lastError) setXeroError(lastError);
      } else setXeroError(errorMessage(res, 'Could not check Xero'));
    } catch (e) {
      setXeroError(errorMessage(e, 'Could not check Xero'));
    }
    setReloadKey((k) => k + 1);
    setChecking(false);
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) return <SeatRequired seat="user" what="Seeing who owes what" />;

  const tabProps: TabProps = { tz, isAdmin, xeroConnected, reloadKey };

  return (
    <>
      <PageHeader
        title="Receivables"
        subtitle="Who owes what, what should arrive next, and what was paid short."
        icon={<Landmark className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/accounts/invoices')}>
              <FileText className="h-4 w-4 mr-1" /> Invoices
            </Button>
            {xeroConnected && (
              <Button variant="outline" size="sm" onClick={checkXero} disabled={checking}>
                <RefreshCw className={cn('h-4 w-4 mr-1', checking && 'animate-spin')} /> {checking ? 'Checking Xero…' : 'Check Xero'}
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <Tabs
          label="Receivables"
          tabs={TABS}
          value={tab}
          onChange={(key) => setParams(key === 'aged' ? {} : { tab: key }, { replace: true })}
        />
        <p className="text-xs text-neutral-500">All amounts inc GST.</p>
        {xeroError && <ErrorBox>Xero could not be checked: {xeroError}</ErrorBox>}

        {tab === 'aged' && <AgedTab {...tabProps} />}
        {tab === 'expected' && <ExpectedTab {...tabProps} />}
        {tab === 'short' && <ShortPaysTab {...tabProps} />}
        {tab === 'payments' && <PaymentsTab {...tabProps} />}
        {tab === 'credits' && <CreditNotesTab {...tabProps} />}
      </div>
    </>
  );
}

export default Receivables;
